import "./index.css";

import React, { useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import zxcvbn from "zxcvbn";
import {
  CheckIcon,
  EyeClosedIcon,
  EyeIcon,
  XIcon,
} from "@primer/octicons-react";

const h = React.createElement;

const strengthLabels = [
  "Very weak",
  "Weak",
  "Still weak",
  "Good enough",
  "Excellent!",
];
const strengthClasses = ["weak", "weak", "fair", "good", "strong"];

function PasswordField({
  autoFocus,
  label,
  name,
  onBlur,
  onChange,
  showToggle = true,
  showPassword,
  togglePassword,
  value,
}) {
  return h("div", { className: "input password required password-form__input" }, [
    h("label", { className: "password required", htmlFor: name, key: "label" }, [
      h("abbr", { title: "required", key: "abbr" }, "*"),
      ` ${label}`,
    ]),
    h("div", { className: "password-form__password-row", key: "row" }, [
      h("input", {
        autoComplete: "new-password",
        autoFocus,
        className: "password required",
        id: name,
        key: "input",
        name,
        onBlur,
        onChange,
        required: true,
        type: showPassword ? "text" : "password",
        value,
      }),
      showToggle
        ? h(
            "button",
            {
              "aria-label": showPassword ? "Hide passwords" : "Show passwords",
              className: "password-form__visibility-toggle",
              key: "button",
              onClick: togglePassword,
              type: "button",
            },
            showPassword
              ? h(EyeIcon, { size: 16 })
              : h(EyeClosedIcon, { size: 16 }),
          )
        : null,
    ]),
  ]);
}

function PasswordForm({ element }) {
  const {
    action = "",
    authenticityToken = "",
    autofocus,
    formClass,
    formId,
    method = "post",
    passwordConfirmationLabel = "Password confirmation",
    passwordLabel = "Password",
    resetPasswordToken,
    scope = "user",
    submitLabel = "Submit",
  } = element.dataset;

  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [passwordConfirmationTouched, setPasswordConfirmationTouched] =
    useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const passwordName = `${scope}[password]`;
  const passwordConfirmationName = `${scope}[password_confirmation]`;

  const strength = useMemo(() => zxcvbn(password), [password]);
  const score = strength.score;
  const passwordWasChanged = passwordTouched || password.length > 0;
  const confirmationWasChanged =
    passwordConfirmationTouched || passwordConfirmation.length > 0;

  const passwordConfirmationError =
    confirmationWasChanged && password !== passwordConfirmation
      ? "Doesn't match password"
      : null;
  const passwordConfirmationMatches =
    confirmationWasChanged && password.length > 0 && password === passwordConfirmation;
  const isValid =
    password.length > 0 && score >= 3 && password === passwordConfirmation;

  const feedback = [strength.feedback.warning, ...strength.feedback.suggestions]
    .filter(Boolean)
    .join(". ")
    .concat(".")
    .replace(/\.{2,}/, ".")
    .replace(/^\.$/, "");

  return h(
    "form",
    {
      action,
      className: ["simple_form", formClass].filter(Boolean).join(" "),
      id: formId,
      method: "post",
      noValidate: true,
    },
    [
      h("input", {
        key: "authenticity-token",
        name: "authenticity_token",
        type: "hidden",
        value: authenticityToken,
      }),
      method.toLowerCase() !== "post"
        ? h("input", {
            key: "method",
            name: "_method",
            type: "hidden",
            value: method,
          })
        : null,
      resetPasswordToken
        ? h("input", {
            key: "reset-password-token",
            name: `${scope}[reset_password_token]`,
            type: "hidden",
            value: resetPasswordToken,
          })
        : null,
      h("div", { className: "form-inputs", key: "inputs" }, [
        h(PasswordField, {
          autoFocus: autofocus === "true",
          key: "password",
          label: passwordLabel,
          name: passwordName,
          onBlur: () => setPasswordTouched(true),
          onChange: (event) => {
            setPassword(event.target.value);
            setPasswordTouched(true);
          },
          showPassword,
          togglePassword: () => setShowPassword((value) => !value),
          value: password,
        }),
        h("div", { className: "zxcvbn password-form__zxcvbn", key: "zxcvbn" }, [
          h("div", { className: "password-form__feedback", key: "feedback" }, [
              passwordWasChanged
                ? h(
                    "div",
                    {
                      className: `password-form__strength ${strengthClasses[score]}`,
                      key: "strength",
                    },
                    [
                      score >= 3
                        ? h(CheckIcon, { key: "icon", size: 16 })
                        : h(XIcon, { key: "icon", size: 16 }),
                      " ",
                      strengthLabels[score],
                    ],
                  )
                : null,
            feedback
              ? h("div", { className: "password-form__feedback", key: "feedback-message" }, feedback)
              : null,
          ]),
        ]),
        h(PasswordField, {
          key: "password-confirmation",
          label: passwordConfirmationLabel,
          name: passwordConfirmationName,
          onBlur: () => setPasswordConfirmationTouched(true),
          onChange: (event) => {
            setPasswordConfirmation(event.target.value);
            setPasswordConfirmationTouched(true);
          },
          showPassword,
          showToggle: false,
          value: passwordConfirmation,
        }),
        passwordConfirmationError
          ? h("div", { className: "password-form__message", key: "password-confirmation-error" }, [
              h(XIcon, { key: "icon", size: 16 }),
              passwordConfirmationError,
            ])
          : null,
        passwordConfirmationMatches
          ? h("div", { className: "password-form__message", key: "password-confirmation-match" }, [
              h(CheckIcon, { key: "icon", size: 16 }),
              "Matches password",
            ])
          : null,
      ]),
      h("div", { className: "form-actions", key: "actions" }, [
        h(
          "button",
          {
            className: "button",
            disabled: !isValid,
            key: "submit",
            type: "submit",
          },
          submitLabel,
        ),
      ]),
    ],
  );
}

document.addEventListener(
  "DOMContentLoaded",
  () => {
    document.querySelectorAll(".password-form-react").forEach((element) => {
      createRoot(element).render(h(PasswordForm, { element }));
    });
  },
  false,
);
